import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Tabs, Select, InputRef, DatePicker, List } from 'antd';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import type { UploadFile } from 'antd/es/upload/interface';
import { VideoCameraOutlined, PictureOutlined, FileTextOutlined, TableOutlined } from '@ant-design/icons';
import {
  showPost,
  editPost,
  createPost,
  storePosts,
  updatePost,
  PostRequest,
  PostApiData,
  Post,
  PostCategory,
} from 'api/post.api';
import { notificationController } from '@app/controllers/notificationController';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { Space } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { useNavigate } from 'react-router-dom';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { BaseImage } from '@app/components/common/BaseImage/BaseImage';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { Tooltip } from 'antd';
import { MdModeEdit } from 'react-icons/md';
import dayjs from 'dayjs';
import { BaseTypography } from '@app/components/common/BaseTypography/BaseTypography';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ColumnsType } from 'antd/lib/table';
import { Assets } from '@app/components/assets/Assets';
import { FileInfo } from '@app/api/assets.api';
import handleErrorMessages from '@app/components/Error/ErrorMsgHandling';
import { IoEye } from 'react-icons/io5';
import useApiErrorHandler from '@app/components/Error/ErrorMsgHandling';

interface TypeOfForm {
  type: 'edit' | 'create' | 'view';
}

interface Video {
  id: number | null;
  thumbnail: string;
  videoLink: string;
  action: string;
}

interface DocFile {
  id: number | null;
  file: UploadFile;
  action: string;
}

interface PostFormProps extends TypeOfForm {
  editable: boolean;
  id?: string;
  // showEditIcon?: boolean;
}

enum Action {
  CREATE = '1',
  DELETE = '2',
}

const { TextArea } = Input;
const { TabPane } = Tabs;
const { Option } = Select;

const formItemLayout = {
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
};

export const extractYouTubeVideoId = (url: string) => {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w\d_-]{11})/);
  return match ? match[1] : null;
};

export const PostForm: React.FC<PostFormProps> = ({ type, id, editable = false }) => {
  const [isFieldsChanged, setFieldsChanged] = useState(false);
  const [form] = BaseButtonsForm.useForm();
  const postId = id ? parseInt(id) : null;
  const { t } = useTranslation();
  const [isLoading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [productType, setProductType] = useState('SI');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [slug, setSlug] = useState<string>('');
  const nameInputRef = useRef<InputRef>(null);
  const [pageTitle, setPageTitle] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [files, setFiles] = useState<string[]>([]);
  const [postFiles, setPostFiles] = useState<FileInfo[]>([]);
  const [postImages, setPostImages] = useState<FileInfo[]>([]);
  const [post, setPost] = useState<Post>();
  const [videoLinkInputValue, setVideoLinkInputValue] = useState('');
  const [videoLinks, setVideoLinks] = useState<Video[]>([]);
  const [postCategories, setPostCategories] = useState<PostCategory[]>([]);
  const [selectedPostCategories, setSelectedPostCategories] = useState<PostCategory[]>([]);
  const [editorHtml, setEditorHtml] = useState('');
  const handleErrorMessages = useApiErrorHandler(); 

  const videoColumns: ColumnsType<Video> = [
    {
      title: 'Video Thumbnail',
      dataIndex: 'thumbnail',
      render: (_: any, record) => (
        <img src={record.thumbnail} alt="Video Thumbnail" style={{ maxWidth: '100px', maxHeight: '100px' }} />
      ),
    },
    {
      title: 'Video Link',
      dataIndex: 'videoLink',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record, index) => (
        <Space size="middle">
          <a onClick={() => handleRemoveVideoLink(index)}>
            <DeleteOutlined />
          </a>
        </Space>
      ),
    },
  ];

  const toolbarOptions = [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],       
    ['blockquote'],
    ['link', 'formula'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'list': 'check' }],
    [{ 'script': 'sub'}, { 'script': 'super' }],     
    [{ 'indent': '-1'}, { 'indent': '+1' }],         
    [{ 'direction': 'rtl' }],                       
    [{ 'color': [] }, { 'background': [] }],        
    [{ 'align': [] }],
    ['clean']                                         
  ];

  const handleVideoLinkInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVideoLinkInputValue(e.target.value);
  };

  const handleRemoveVideoLink = (rowIndex: number) => {
    const updatedVideoLinks = videoLinks?.filter((_, index) => index !== rowIndex);
    const deletedItem = videoLinks?.find((_, index) => index === rowIndex);
    const item: Video = {
      id: deletedItem?.id ?? null,
      action: Action.DELETE,
      thumbnail: '',
      videoLink: '',
    };

    setVideoLinks([...updatedVideoLinks, item]);
  };

  const handleNameChange = (e: any) => {
    const a = nameInputRef?.current?.input?.value ?? '';
    const b = a.replace(/\s+/g, ' ').trim().replaceAll(' ', '_').toLowerCase();
    form.setFieldValue('slug', b);
  };

  const getPopupContainer = (triggerNode: HTMLElement | null) => {
    return triggerNode || document.body;
  };

  const handleSubmit = async (values: PostRequest) => {
    setLoading(true);

    const formattedValues = {
      ...values,
      image: imageUrl,
      display_at: dayjs(values.display_at).format('YYYY-MM-DD'),
      video_links: videoLinks?.map((obj) => {
        return { id: obj.id, video_link: obj.videoLink, action: obj.action };
      }),
      files: postFiles?.filter(e => e.action !== '2').map(e => {return {name: e.name, action: e.action}}),
      images: postImages?.filter(e => e.action !== '2').map(e => {return {name: e.name, action: e.action}})
    };

    console.log('submitted');
    console.log(formattedValues);

    if (editable) {
      if (type === 'create') {
        await storePosts(formattedValues)
          .then((response) => {
            setLoading(false);
            console.log(response);
            navigate(`/show-post/${response?.data?.id}`);
          })
          .catch((error) => {
            setLoading(false);
            handleErrorMessages(error);
            console.error(error);
          }); 
      } else if (type === 'edit' && postId) {
        await updatePost(postId, formattedValues)
          .then((response) => {
            setLoading(false);
            console.log(response);
            navigate(`/show-post/${response?.data?.id}`);
          })
          .catch((error) => {
            setLoading(false);
            handleErrorMessages(error);
            console.error(error);
          });
      }
    }
  };

  const handleTypeChange = (e: any) => {
    setProductType(e);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setImageFile(file);

    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setSelectedImage(event.target?.result as string);
        setImageUrl(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditIconClick = () => {
    navigate(`/edit-post/${id}`);
  };

  const setApiData = (res: PostApiData) => {

    console.log(res);
    setPostFiles(res?.files);
    setPostImages(res?.images);
    setPost(res?.post);
    setImageUrl(res?.post?.image ?? '');
    setSelectedImage(res?.post?.image_url ?? 'error')
    setPostCategories(res?.category_options);

    const videos = res?.post?.videos.map((e) => {
      const youtubeVideoId = extractYouTubeVideoId(e.video_link);
      const thumbnailUrl = `https://img.youtube.com/vi/${youtubeVideoId}/mqdefault.jpg`;
      return { id: e.id ?? null, videoLink: e.video_link, thumbnail: thumbnailUrl, action: Action.CREATE };
    });
    setVideoLinks(videos);


    form.setFieldsValue({
      title: res?.post?.title,
      slug: res?.post?.slug,
      body: res?.post?.body,
      published_at: res?.post?.published_at != null ? dayjs(res?.post?.published_at): dayjs(),
      image: res?.post?.image_url,
      category_ids: res?.post?.category_ids,
      display_at: res?.post?.display_at != null ? dayjs(res?.post?.display_at): dayjs(),
    });
  };

  useEffect(() => {
    setLoading(true)
    if (postId) {
      if (type === 'view') {
        showPost(postId).then((res: any) => {
          console.log(res);
          setApiData(res);
          setPageTitle(`${res?.post?.title}`);
          setLoading(false);
        })
        .catch((error) =>  handleErrorMessages(error));
      } else if (type === 'edit') {
        editPost(postId).then((res: any) => {
          console.log('edit =>' + res);
          setLoading(false);
          setApiData(res);
          setPageTitle(`${res?.post?.title}`);
        })
        .catch((error) =>  handleErrorMessages(error));
      }
    } else {
      createPost().then((res: any) => {
        setLoading(false);
        setApiData(res);
        setPageTitle(`New`);
      })
      .catch((error) =>   handleErrorMessages(error));
    }
  }, []);

  const addVideoLink = () => {
    if (videoLinkInputValue.trim() !== '') {
      const youtubeVideoId = extractYouTubeVideoId(videoLinkInputValue);
      if (youtubeVideoId) {
        const thumbnailUrl = `https://img.youtube.com/vi/${youtubeVideoId}/mqdefault.jpg`;
        const video :Video = { id: null, videoLink: videoLinkInputValue.trim(), thumbnail: thumbnailUrl, action: Action.CREATE };
        const existingList : Video[] = videoLinks ?? [];
        setVideoLinks([video, ...existingList]);
        setVideoLinkInputValue('');
      } else {
        // Handle invalid YouTube URL
        notificationController.error({ message: 'Invalid YouTube URL' });
      }
    }
  };

  return (
    <>
      <BaseRow gutter={[16, 16]}>
        <BaseCol xs={24} md={24} lg={16}>
          <BaseCard
            id="validation form"
            // title={pageTitle}
            title={`Post - `+  pageTitle || 'New'}
            padding="1.25rem"
            style={{ margin: '20px', marginTop: '0px', width: '98%' }}
            extra={
              <>
             { 
                type === 'view' && (
                  <>
                    <Space>
                      <Tooltip title={t('common.edit')}>
                        <BaseButton type="text" onClick={() => navigate(`/edit-post/${id}`)} icon={<MdModeEdit />} />
                      </Tooltip>
                    </Space>
                  </>
                )
              ||
                type === 'edit' && (
                  <>
                    <Space>
                      <Tooltip title={t('view')}>
                        <BaseButton type="text" onClick={() => navigate(`/show-post/${id}`)} icon={<IoEye />} />
                      </Tooltip>
                    </Space>
                  </>
                )
              }
              <Space>
                <Tooltip title={'View all'}>
                  <BaseButton type="text" onClick={() => navigate('/post')} icon={<TableOutlined />} />
                </Tooltip>
              </Space>
            </> 
              
            }
          >
            <BaseButtonsForm
              {...formItemLayout}
              name="postsForm"
              onFinish={handleSubmit}
              form={form}
              loading={isLoading}
              isFieldsChanged={isFieldsChanged}
              setFieldsChanged={setFieldsChanged}
            >
              {/* {type === 'view' && showEditIcon && (
                <Tooltip title={t('common.edit')}>
                  <MdModeEdit onClick={handleEditIconClick} />
                </Tooltip>
              )} */}
              <BaseRow gutter={[16, 16]}>
                <BaseCol xs={24} md={8} lg={18}>
                  <BaseButtonsForm.Item
                    name="title"
                    label="Title"
                    key="title"
                    rules={[{ required: true, message: 'Title is required' }]}
                  >
                    <BaseInput
                      ref={nameInputRef}
                      placeholder="Enter title"
                      disabled={!editable}
                      onChange={handleNameChange}
                    />
                  </BaseButtonsForm.Item>
                </BaseCol>
              </BaseRow>

              <BaseRow gutter={[16, 16]}>
                <BaseCol xs={24} md={8} lg={18}>
                  <BaseButtonsForm.Item
                    name="slug"
                    label="Slug"
                    key="slug"
                    rules={[{ required: true, message: 'Slug is required' }]}
                  >
                    <BaseInput placeholder="Enter Slug" disabled={!editable} value={slug} />
                  </BaseButtonsForm.Item>
                </BaseCol>
              </BaseRow>

              <BaseRow gutter={[16, 16]}>
                <BaseCol xs={24} md={24} lg={18}>
                  <BaseButtonsForm.Item name="category_ids" label="Post Categories">
                    <BaseSelect
                      placeholder="Select Post Categories"
                      disabled={!editable}
                      allowClear
                      mode="multiple"
                      onChange={(selectedOptions: any) => {
                        const selectedCategories = postCategories.filter((category) =>
                          selectedOptions.includes(category.id),
                        );
                        setSelectedPostCategories(selectedCategories);
                      }}
                    >
                      {postCategories.map((category) => (
                        <Select.Option key={category.id} value={category.id}>
                          {category.name}
                        </Select.Option>
                      ))}
                    </BaseSelect>
                  </BaseButtonsForm.Item>
                </BaseCol>
              </BaseRow>

              <BaseRow gutter={[16, 16]}>
                <BaseCol xs={24} md={8} lg={18}>
                  <BaseButtonsForm.Item
                    name="body"
                    label="Body"
                    key="body"
                    rules={[{ required: true, message: 'Body is required' }]}
                  >
                    <ReactQuill modules={{toolbar:toolbarOptions}} theme="snow" value={editorHtml} onChange={setEditorHtml} readOnly={type === 'view'} />
                  </BaseButtonsForm.Item>
                </BaseCol>
              </BaseRow>

              <BaseRow gutter={[16, 16]}>
                <BaseCol xs={6} md={12} lg={12}>
                  <BaseButtonsForm.Item label="Display Date" name="display_at">
                    <DatePicker
                      style={{ display: 'flex', flexGrow: 1 }}
                      disabled={!editable}
                      format="MMMM D, YYYY"
                      use12Hours
                    />
                  </BaseButtonsForm.Item>
                </BaseCol>
              </BaseRow>

              <BaseRow gutter={[16, 16]}>
                <BaseCol xs={24} md={8} lg={12}>
                  <BaseButtonsForm.Item name="image" label="Thumbnail Image">
                    <input type="file" accept="image/*" onChange={handleFileChange} hidden={!editable} />
                    <br />
                    <BaseImage
                      preview={false}
                      src={selectedImage ?? 'error'}
                      style={{ width: 200, height: 200 }}
                      fallback="data:image/png;base64,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"
                    ></BaseImage>
                  </BaseButtonsForm.Item>
                </BaseCol>
              </BaseRow>
              
              <br></br>
              <Tabs defaultActiveKey="1">
                <TabPane
                  tab={
                    <span>
                      <VideoCameraOutlined />
                      {'Post Videos'}
                    </span>
                  }
                  key="1"
                >
                  {type !== 'view' && (
                    <>
                      <BaseButtonsForm.Item label="Video Link">
                        <BaseInput
                          placeholder="Enter Video Link"
                          value={videoLinkInputValue}
                          onChange={handleVideoLinkInputChange}
                        />
                      </BaseButtonsForm.Item>
                      <BaseButton type="primary" onClick={addVideoLink}>
                        Add Video Link
                      </BaseButton>
                    </>
                  )}

                  <br />
                  <br />
                  <BaseTable
                    bordered
                    dataSource={videoLinks?.filter((e) => e.action !== '2')}
                    columns={type !== 'view' ? videoColumns : videoColumns.slice(0, 2)}
                    pagination={false}
                  />
                </TabPane>

                <TabPane
                  tab={
                    <span>
                      <FileTextOutlined />
                      {t('Post Files')}
                    </span>
                  }
                  key="2"
                >
                  <Assets type="files" code='post_document'  targetFiles={setPostFiles} editable={editable} previouslySelectedFiles={postFiles}/>
                </TabPane>

                <TabPane
                  tab={
                    <span>
                      <PictureOutlined />
                      {t('Post Images')}
                    </span>
                  }
                  key="3"
                >
                  <Assets type="images" code='post_image'  targetFiles={setPostImages} editable={editable} previouslySelectedFiles={postImages}/>
                </TabPane>
              </Tabs>
              <br />
              <br />
              <br></br>
              <BaseButtonsForm.Item>
                <BaseButton type="primary" htmlType="submit" loading={isLoading} disabled={!editable}>
                  {t('Save Post')}
                </BaseButton>
              </BaseButtonsForm.Item>
            </BaseButtonsForm>
          </BaseCard>
        </BaseCol>

        <BaseCol xs={24} lg={6}>
          <BaseCard
            title={t('Post Setting')}
            padding={'1.25rem'}
            style={{
              width: '100%',
              height: 'fit-content',
            }}
          >
            <List size={'small'}>
              <List.Item>
                <Space direction="vertical">
                  <BaseTypography.Text style={{ color: 'var(--primary-color)', fontSize: '14px' }}>
                    Published on:
                  </BaseTypography.Text>
                </Space>
              </List.Item>
              <List.Item>
                <Space direction="vertical">
                  <BaseTypography.Text style={{ color: 'var(--primary-color)', fontSize: '14px' }}>
                    Status:
                  </BaseTypography.Text>
                </Space>
              </List.Item>
              <List.Item>
                <Space direction="vertical">
                  <BaseTypography.Text
                    style={{ color: 'var(--primary-color)', fontSize: '14px' }}
                  ></BaseTypography.Text>
                </Space>
              </List.Item>
            </List>
          </BaseCard>
        </BaseCol>
      </BaseRow>
    </>
  );
}

export default PostForm;
